import { INSTRUMENTS } from "connected-components/dashboard/helpers"
import type { DrilldownFrozenSnapshot } from "./types"

/**
 * Resolves which instrument "series" a drilldown must gather and render from its frozen snapshot.
 *
 * The drilldown reuses the asset-view metrics hook's three slots — original / rebalanced /
 * comparison — and the canonical column labels (original→O or E, rebalanced→R, comparison→C, or
 * T when an optimization, or S under the end-vs-start flag). Two product gaps motivate this:
 *   - end-vs-start: the start column is the SAME portfolio gathered as-of the start date,
 *     placed in the comparison slot (which the hook relabels S: when isEndVsStartDateComparison).
 *   - rebalanced / optimized: secondInstrument was captured but never rendered.
 *
 * Mode is derived from the instruments already on the snapshot rather than a stored mode (the
 * explicit taxonomy lives in dashboard/helpers as SelectedMode / INSTRUMENTS_BY_MODE): a present
 * comparisonInstrument is the benchmark/target; a secondInstrument is the rebalanced portfolio, or
 * the optimization target when it carries isOptimization. Scope is intentionally the four
 * single-second-column modes — combined modes (e.g. compare + end-vs-start) are out of scope and
 * keep today's single-second-column behavior.
 *
 * This module is pure (no React / no data fetching) so the slot/column/as-of mapping is unit
 * tested directly. Whether the gathered start vs end (or original vs rebalanced) values actually
 * differ is a data concern verified by the e2e/QA layer, not here.
 */

type InstrumentLike = Record<string, unknown>
export type DrilldownInstrumentSlot = "original" | "rebalanced" | "comparison"

export interface DrilldownInstrumentSeries {
  /** Hook slot — drives the column label (O/E, R, C/T/S) and the gatherer/data-path suffix. */
  instrumentType: DrilldownInstrumentSlot
  /** Instrument object to gather. */
  positionSet: InstrumentLike
  /** Composition/holdings are gathered as-of this date (end date for most series, start date for
   *  the end-vs-start comparison series). */
  asOf: string
  /** Gatherer prefix / data-path suffix, e.g. "" | "-rebalanced" | "-comparison". */
  suffix: string
}

export interface ResolvedDrilldownInstruments {
  /** Ordered columns for the metrics hook — always original-first to match the BDD. */
  instrumentColumns: string[]
  /** Series that need gatherers (original plus an optional second slot). */
  series: DrilldownInstrumentSeries[]
  /** Instruments to pass to the hook's rebalanced / comparison slots. */
  rebalancedInstrument: InstrumentLike | null
  comparisonInstrument: InstrumentLike | null
  /** Per-slot label metadata (names, optimization flag) for the hook. */
  instrumentsWithMetadata: Record<string, InstrumentLike> | null
}

function isOptimization(instrument: InstrumentLike | null | undefined): boolean {
  return Boolean(instrument?.isOptimization)
}

function instrumentName(instrument: InstrumentLike | null | undefined, fallback: string): string {
  return typeof instrument?.name === "string" ? instrument.name : fallback
}

export function resolveDrilldownInstruments(snapshot: DrilldownFrozenSnapshot): ResolvedDrilldownInstruments {
  const { positionSet, comparisonInstrument, secondInstrument, startDate, endDate, isEndVsStartDateComparison } = snapshot
  const original: InstrumentLike = positionSet ?? {}

  const originalSeries: DrilldownInstrumentSeries = {
    instrumentType: "original",
    positionSet: original,
    asOf: endDate,
    suffix: INSTRUMENTS.original.metricsSuffix,
  }

  let secondSeries: DrilldownInstrumentSeries | null = null
  let rebalancedInstrument: InstrumentLike | null = null
  let comparisonForHook: InstrumentLike | null = null

  if (isEndVsStartDateComparison) {
    // Same portfolio at two dates: the start column lives in the comparison slot and is gathered
    // as-of startDate; the hook relabels original→E and comparison→S under the flag.
    secondSeries = { instrumentType: "comparison", positionSet: original, asOf: startDate, suffix: INSTRUMENTS.comparison.metricsSuffix }
    comparisonForHook = original
  } else if (comparisonInstrument) {
    secondSeries = { instrumentType: "comparison", positionSet: comparisonInstrument, asOf: endDate, suffix: INSTRUMENTS.comparison.metricsSuffix }
    comparisonForHook = comparisonInstrument
  } else if (secondInstrument && isOptimization(secondInstrument)) {
    // Optimized vs original — target rendered in the comparison slot, labelled "T:" via isOptimization.
    secondSeries = { instrumentType: "comparison", positionSet: secondInstrument, asOf: endDate, suffix: INSTRUMENTS.comparison.metricsSuffix }
    comparisonForHook = secondInstrument
  } else if (secondInstrument) {
    // Rebalanced vs original — rebalanced portfolio rendered in the rebalanced slot ("R:").
    secondSeries = { instrumentType: "rebalanced", positionSet: secondInstrument, asOf: endDate, suffix: INSTRUMENTS.rebalanced.metricsSuffix }
    rebalancedInstrument = secondInstrument
  }

  if (!secondSeries) {
    return {
      instrumentColumns: ["original"],
      series: [originalSeries],
      rebalancedInstrument: null,
      comparisonInstrument: null,
      instrumentsWithMetadata: null,
    }
  }

  // Original first, then the second column, then the delta — matches the BDD's
  // "Original / {Rebalanced|Target|Comparison|Start} / Delta".
  const instrumentColumns = ["original", secondSeries.instrumentType, "delta"]

  const originalMeta: InstrumentLike = { name: instrumentName(original, "Portfolio") }
  const instrumentsWithMetadata: Record<string, InstrumentLike> = secondSeries.instrumentType === "rebalanced"
    ? {
      original: originalMeta,
      rebalanced: { name: instrumentName(secondInstrument, "Rebalanced") },
    }
    : {
      original: originalMeta,
      comparison: {
        type: comparisonForHook?.type,
        name: instrumentName(comparisonForHook, isEndVsStartDateComparison ? "Start" : "Comparison"),
        isOptimization: isOptimization(comparisonForHook),
      },
    }

  return {
    instrumentColumns,
    series: [originalSeries, secondSeries],
    rebalancedInstrument,
    comparisonInstrument: comparisonForHook,
    instrumentsWithMetadata,
  }
}
